import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "../../api";
import ItemDrawer from "./ItemDrawer";
import QueueBoard from "./QueueBoard";
import QueueCalendar from "./QueueCalendar";
import { AUTHOR_OPTIONS, CHANNEL_LABEL, STATE_LABEL, STATE_ORDER } from "./labels";

const VIEW_KEY = "dsl_marketing_queue_view";
// Board needs four columns side by side; below this it stops being a board.
const BOARD_MIN_WIDTH = 900;

export default function Queue({ isAdmin, onChanged, onOpenCheck }) {
  const [items, setItems] = useState([]);
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openId, setOpenId] = useState(null);
  const [filters, setFilters] = useState({ state: "", channel: "", author: "", athlete: "" });
  // Folded by default. Four controls at 130px each wrap to four rows on a
  // phone, above the content they filter — so the first thing Ward saw when
  // he opened the Queue was the machinery for narrowing it, not the queue.
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [wide, setWide] = useState(() => window.innerWidth >= BOARD_MIN_WIDTH);
  const [view, setView] = useState(() => localStorage.getItem(VIEW_KEY) || "list");
  const [reloadKey, setReloadKey] = useState(0);
  // Synchronous re-entrancy guard for `move`, same reasoning as ItemDrawer's
  // `inFlight`: `/transition` is the only writer of the audit trail, and a
  // drag-and-drop drop handler can fire twice just as easily as a double
  // click can — a state flag alone is not synchronous enough to close that
  // window, so this ref is checked and set before the first `await`.
  const moveInFlight = useRef(false);
  // Same guard for `add`. A native double-click on the Add button lands two
  // POSTs milliseconds apart and the shared queue grows a duplicate; `add`
  // has no `disabled` affordance to race against at all, so the ref is the
  // only thing standing between an impatient click and a duplicate item.
  const addInFlight = useRef(false);

  useEffect(() => {
    const onResize = () => setWide(window.innerWidth >= BOARD_MIN_WIDTH);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const chooseView = (v) => { setView(v); localStorage.setItem(VIEW_KEY, v); };
  // List is the default and the only view that survives a phone screen.
  const effectiveView = wide ? view : "list";

  const load = useCallback(() => {
    const params = Object.fromEntries(
      Object.entries(filters).filter(([, v]) => v));
    return api.get("/marketing/items", { params })
      .then(({ data }) => { setItems(data.items); setError(""); })
      .catch((e) => setError(formatApiErrorDetail(e?.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/marketing/checks").then(({ data }) => setChecks(data.checks)).catch(() => setChecks([]));
  }, []);

  const refresh = useCallback(async () => {
    await load();
    await onChanged();
    setReloadKey((k) => k + 1);
  }, [load, onChanged]);

  const add = async (e) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    if (addInFlight.current) return;
    addInFlight.current = true;
    setError("");
    try {
      const { data } = await api.post("/marketing/items", { title: newTitle.trim() });
      setNewTitle("");
      await refresh();
      setOpenId(data.id);
    } catch (e2) {
      // The typed title stays in the box on failure — retyping it is the
      // one thing that would make a failed Add worse than it already is.
      setError(formatApiErrorDetail(e2?.response?.data?.detail));
    } finally {
      addInFlight.current = false;
    }
  };

  // A drag issues the same transition a button does — one path into the state
  // machine, so the trail records a drag identically to a click. The failure
  // is reported here rather than in the board, so the reason the server gave
  // ("Cannot move from published to review") survives instead of being
  // flattened into a generic sentence by the caller.
  const move = async (id, to) => {
    if (moveInFlight.current) return;
    moveInFlight.current = true;
    setError("");
    try {
      await api.post(`/marketing/items/${id}/transition`, { to });
      await refresh();
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      moveInFlight.current = false;
    }
  };

  // A filter that is set has to say so, or the Queue is missing items for a
  // reason that is folded away. The count on the button is that signal, and
  // Clear sits beside it — so folding stays honest and the button keeps
  // working. Forcing the row open instead made the toggle do nothing.
  const active = Object.values(filters).filter(Boolean).length;

  const open = items.find((i) => i.id === openId) || null;
  const grouped = STATE_ORDER
    .map((s) => [s, items.filter((i) => i.state === s)])
    .filter(([, rows]) => rows.length);

  return (
    <div data-testid="mk-queue">
      <form className="mk-newrow" onSubmit={add}>
        <input className="mk-input" placeholder="New idea — one line is enough"
          value={newTitle} onChange={(e) => setNewTitle(e.target.value)}
          data-testid="mk-new-title" />
        <button type="submit" className="mk-btn">Add</button>
      </form>

      <div className="mk-filterbar">
        <button
          type="button"
          className={`mk-btn-quiet ${filtersOpen || active > 0 ? "on" : ""}`}
          aria-expanded={filtersOpen}
          onClick={() => setFiltersOpen((v) => !v)}
          data-testid="mk-filters-toggle"
        >
          Filter{active > 0 ? ` · ${active}` : ""}
        </button>
        {active > 0 && (
          <button type="button" className="mk-btn-quiet"
            onClick={() => setFilters({ state: "", channel: "", author: "", athlete: "" })}
            data-testid="mk-filters-clear">Clear</button>
        )}
        {wide && (
          <div className="mk-viewtoggle" data-testid="mk-viewtoggle">
            {["list", "board", "calendar"].map((v) => (
              <button key={v} type="button"
                className={`mk-viewbtn ${effectiveView === v ? "on" : ""}`}
                onClick={() => chooseView(v)} data-testid={`mk-view-${v}`}>
                {v}
              </button>
            ))}
          </div>
        )}
        {/* The toggle used to just vanish under 900px with nothing said, so
            board and calendar looked like they had been taken away. */}
        {!wide && (
          <span className="mk-viewnote" data-testid="mk-viewnote">
            Board and calendar need a wider screen
          </span>
        )}
      </div>

      {filtersOpen && (
      <div className="mk-filters" data-testid="mk-filters">
        <select className="mk-select" value={filters.state}
          onChange={(e) => setFilters({ ...filters, state: e.target.value })}>
          <option value="">All states</option>
          {STATE_ORDER.map((s) => <option key={s} value={s}>{STATE_LABEL[s]}</option>)}
        </select>
        <select className="mk-select" value={filters.channel}
          onChange={(e) => setFilters({ ...filters, channel: e.target.value })}>
          <option value="">All channels</option>
          {Object.entries(CHANNEL_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <select className="mk-select" value={filters.author}
          onChange={(e) => setFilters({ ...filters, author: e.target.value })}>
          <option value="">Any author</option>
          {AUTHOR_OPTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <input className="mk-input" placeholder="Athlete" value={filters.athlete}
          onChange={(e) => setFilters({ ...filters, athlete: e.target.value })} />
      </div>
      )}

      {error && <div className="mk-error">{error}</div>}
      {loading && <div className="disc-empty">Loading…</div>}
      {!loading && !items.length && (
        <div className="disc-empty">Nothing in the queue yet. Add a line above.</div>
      )}

      {effectiveView === "list" && grouped.map(([state, rows]) => (
        <div key={state} className="mk-group">
          <div className="mk-group-head">
            {STATE_LABEL[state]} <span className="mk-count">{rows.length}</span>
          </div>
          {rows.map((it) => (
            <button key={it.id} type="button" className="mk-row"
              onClick={() => setOpenId(it.id)} data-testid={`mk-row-${it.id}`}>
              <span className="mk-row-title">{it.title}</span>
              <span className="mk-row-meta">
                {it.athlete_name && <span className="mk-tag">{it.athlete_name}</span>}
                {(it.channels || []).map((c) => (
                  <span key={c} className="mk-tag">{CHANNEL_LABEL[c]}</span>
                ))}
                {it.planned_for && <span className="mk-tag date">{it.planned_for}</span>}
                <span className={`mk-pill author-${it.author}`}>{it.author}</span>
              </span>
            </button>
          ))}
        </div>
      ))}

      {effectiveView === "board" && (
        <QueueBoard items={items} onOpen={setOpenId} onMove={move} />
      )}

      {effectiveView === "calendar" && (
        // `refresh`, not the bare `onChanged` prop: a calendar drag PATCHes
        // planned_for and reloads the calendar's own scheduled/unscheduled
        // state, but Queue's own `items` array — what the drawer opened via
        // `onOpen` actually reads — is a separate fetch. Without routing
        // through `refresh` here, the drawer opened from the calendar shows
        // a stale (pre-drag) planned_for until something else in the list
        // happens to reload it.
        <QueueCalendar onOpen={setOpenId} onChanged={refresh} reloadKey={reloadKey} />
      )}

      {open && (
        <ItemDrawer item={open} checks={checks} isAdmin={isAdmin}
          onClose={() => setOpenId(null)} onChanged={refresh}
          onOpenCheck={onOpenCheck} />
      )}
    </div>
  );
}
